import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with defaults
const api = axios.create({
    baseURL: `${API}/api`,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Error handler
export function formatApiError(error) {
    const detail = error.response?.data?.detail;
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail === "object") {
        if (typeof detail.msg === "string") return detail.msg;
        if (typeof detail.message === "string") return detail.message;
        if (typeof detail.code === "string") return detail.code;
    }
    return String(detail);
}

// Extracts the structured `code` from a `{code, message}` HTTPException
// so callers can localize via t(`auth.errors.${code}`) instead of
// displaying the backend's fallback message verbatim.
export function extractApiErrorCode(error) {
    const detail = error.response?.data?.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail) && typeof detail.code === "string") {
        return detail.code;
    }
    return null;
}

// Jobs API
export const jobsApi = {
    getAll: (status, skip = 0, limit = 20, owner) => api.get('/jobs', { params: { status, skip, limit, owner } }),
    getById: (id) => api.get(`/jobs/${id}`),
    create: (data) => api.post('/jobs', data),
    update: (id, data) => api.put(`/jobs/${id}`, data),
    delete: (id) => api.delete(`/jobs/${id}`),
    updateStatus: (id, status) => api.patch(`/jobs/${id}/status`, null, { params: { status } }),
    analyzeBatch: (jobId, candidateIds = null) => api.post(`/jobs/${jobId}/analyze-batch`, candidateIds ? { candidate_ids: candidateIds } : undefined),
    analyzeBatchPreview: (jobId) => api.get(`/jobs/${jobId}/analyze-batch/preview`),
    getAnalyzeProgressUrl: (taskId) => `${API}/api/jobs/analyze-progress/${taskId}`,
    getTrash: (params = {}) => api.get('/jobs/trash/list', { params }),
    restore: (id) => api.post(`/jobs/${id}/restore`),
    purge: (id) => api.delete(`/jobs/${id}/purge`),
};

// Candidates API
export const candidatesApi = {
    getAll: (params) => api.get('/candidates', { params }),
    getTopCandidates: (skip = 0, limit = 20) => api.get('/candidates', { params: { top_only: true, skip, limit } }),
    getById: (id) => api.get(`/candidates/${id}`),
    upload: (file, jobId) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/candidates/upload', formData, {
            params: { job_id: jobId },
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    uploadSSE: (file, jobId, force = false) => {
        const formData = new FormData();
        formData.append('file', file);
        const params = {};
        if (jobId) params.job_id = jobId;
        if (force) params.force = 'true';
        return api.post('/candidates/upload-sse', formData, {
            params,
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    analyze: (id) => api.post(`/candidates/${id}/analyze`),
    delete: (id) => api.delete(`/candidates/${id}`),
    matchToJob: (candidateId, jobId) => api.patch(`/candidates/${candidateId}/match`, null, { params: { job_id: jobId } }),
    getSimilar: (id, limit) => api.get(`/candidates/${id}/similar`, { params: { limit } }),
    downloadCv: (id) => api.get(`/candidates/${id}/cv`, { responseType: 'blob' }),
    exportCandidates: (params) => api.get('/candidates/export', { params, responseType: 'blob' }),
    semanticSearch: (query, options = {}) => api.post('/candidates/semantic-search', { query, ...options }),
    exportPdf: (id) => api.get(`/candidates/${id}/export-pdf`, { responseType: 'blob' }),
    getPipelineBoard: (jobId) => api.get('/candidates/pipeline/board', { params: jobId ? { job_id: jobId } : {} }),
    movePipeline: (id, slug) => api.patch(`/candidates/${id}/pipeline`, { slug }),
    updateTags: (id, action, tags) => api.patch(`/candidates/${id}/tags`, { action, tags }),
    getAllTags: () => api.get('/candidates/tags/all'),
    getTrash: (params = {}) => api.get('/candidates/trash/list', { params }),
    restore: (id) => api.post(`/candidates/${id}/restore`),
    purge: (id) => api.delete(`/candidates/${id}/purge`),
};

// Tags registry API (organisation-scoped, categorized, colored)
export const tagsApi = {
    list: (category) => api.get('/tags', { params: category ? { category } : {} }),
    create: (name, category) => api.post('/tags', { name, category }),
    update: (id, payload) => api.patch(`/tags/${id}`, payload),
    delete: (id) => api.delete(`/tags/${id}`),
};

// Organizations API
export const organizationsApi = {
    getCurrent: () => api.get('/organizations/current'),
    update: (data) => api.put('/organizations/current', data),
    getMembers: () => api.get('/organizations/members'),
    getMembersUsage: () => api.get('/organizations/members/usage'),
    invite: (email, role) => api.post('/organizations/invite', null, { params: { email, role } }),
    removeMember: (userId) => api.delete(`/organizations/members/${userId}`),
};

// Subscriptions API (legacy + real subscription-mode)
export const subscriptionsApi = {
    getPlans: () => api.get('/subscriptions/plans'),
    getCurrent: () => api.get('/subscriptions/current'),
    createCheckout: (planId, originUrl) => api.post('/subscriptions/checkout', { plan_id: planId, origin_url: originUrl }),
    getCheckoutStatus: (sessionId) => api.get(`/subscriptions/checkout/status/${sessionId}`),
    changePlan: (planId) => api.post('/subscriptions/change-plan', { plan_id: planId }),
    validateChange: (planId) => api.get(`/subscriptions/validate-change/${planId}`),
    getTransactions: (skip = 0, limit = 20) => api.get('/billing/transactions', { params: { skip, limit } }),
};

// Billing API (real Stripe subscription-mode)
export const billingApi = {
    listProviders: () => api.get('/billing/providers'),
    createCheckoutSession: (planId, originUrl, provider = 'stripe', currency = 'eur') =>
        api.post('/billing/create-checkout-session', { plan_id: planId, origin_url: originUrl, provider, currency }),
    changeSubscription: (planId) => api.post('/billing/change-subscription', { plan_id: planId }),
    createPortalSession: (returnUrl) => api.post('/billing/portal-session', { return_url: returnUrl }),
    getTransactions: (skip = 0, limit = 50) => api.get('/billing/transactions', { params: { skip, limit } }),
    listOveragePacks: () => api.get('/billing/overage-packs'),
    createOverageCheckout: (packId, originUrl, provider = 'stripe', currency = 'eur') =>
        api.post('/billing/overage-checkout', { pack_id: packId, origin_url: originUrl, provider, currency }),
};

// Analytics API
export const analyticsApi = {
    getDashboard: () => api.get('/analytics/dashboard'),
    getJobAnalytics: (jobId) => api.get(`/analytics/job/${jobId}`),
    getPipelineAnalytics: (params = {}) => api.get('/analytics/pipeline', { params }),
};

// Notifications API
export const notificationsApi = {
    getAll: (skip = 0, limit = 20) => api.get('/notifications', { params: { skip, limit } }),
    getUnreadCount: () => api.get('/notifications/unread-count'),
    markRead: (id) => api.patch(`/notifications/${id}/read`),
    markAllRead: () => api.patch('/notifications/read-all'),
    getStreamUrl: () => `${API}/api/notifications/stream`
};

// Admin API
export const adminApi = {
    getUsers: (params = {}) => api.get('/admin/users', { params }),
    getUserDetail: (userId) => api.get(`/admin/users/${userId}`),
    getOrganizations: (params = {}) => api.get('/admin/organizations', { params }),
    getOrganizationsList: () => api.get('/admin/organizations/list'),
    updateUserRole: (userId, role) => api.patch(`/admin/users/${userId}/role`, null, { params: { role } }),
    updateUserStatus: (userId, status) => api.patch(`/admin/users/${userId}/status`, null, { params: { status } }),
    deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
    updateOrgPlan: (orgId, plan) => api.patch(`/admin/organizations/${orgId}/plan`, null, { params: { plan } }),
    generateCheckoutLink: (orgId, plan, originUrl) => api.post(`/admin/organizations/${orgId}/checkout-link`, null, { params: { plan, origin_url: originUrl } }),
    getStats: () => api.get('/admin/stats'),
    getGlobalAnalytics: (params = {}) => api.get('/admin/global-analytics', { params }),
    getTransactions: (params = {}) => api.get('/admin/transactions', { params }),
    getUsersTrash: (params = {}) => api.get('/admin/users/trash/list', { params }),
    restoreUser: (userId) => api.post(`/admin/users/${userId}/restore`),
    purgeUser: (userId) => api.delete(`/admin/users/${userId}/purge`),
    getOrganizationsTrash: (params = {}) => api.get('/admin/organizations/trash', { params }),
    softDeleteOrganization: (orgId) => api.delete(`/admin/organizations/${orgId}`),
    restoreOrganization: (orgId) => api.post(`/admin/organizations/${orgId}/restore`),
    permanentlyDeleteOrganization: (orgId, confirmName) => api.delete(`/admin/organizations/${orgId}/permanent`, { params: { confirm_name: confirmName } }),
    getScoringCacheStats: () => api.get('/admin/scoring-cache/stats'),
    clearScoringCache: () => api.delete('/admin/scoring-cache'),
    invalidateScoringCacheForCandidate: (candidateId) => api.delete(`/admin/scoring-cache/candidate/${candidateId}`),
    invalidateScoringCacheForJob: (jobId) => api.delete(`/admin/scoring-cache/job/${jobId}`),
    invalidateScoringCacheForPair: (candidateId, jobId, engine = 'all') => api.delete(`/admin/scoring-cache/pair/${candidateId}/${jobId}`, { params: { engine } }),
    getScoringEngineStatus: () => api.get('/admin/scoring-engine/status'),
    toggleScoringEngine: (orgId) => api.post(`/admin/scoring-engine/${orgId}/toggle`),
    scoringCompareEstimate: (body) => api.post('/admin/scoring-compare/estimate', body),
    scoringCompareStart: (body) => api.post('/admin/scoring-compare/runs', body),
    scoringCompareRuns: () => api.get('/admin/scoring-compare/runs'),
    scoringCompareResults: (runId) => api.get(`/admin/scoring-compare/runs/${runId}/results`),
    scoringCompareCsv: (runId) => api.get(`/admin/scoring-compare/runs/${runId}/export.csv`, { responseType: 'blob' }),
    scoringCompareCancel: (runId) => api.post(`/admin/scoring-compare/runs/${runId}/cancel`),
    scoringCompareResume: (runId, maxCost) => api.post(`/admin/scoring-compare/runs/${runId}/resume`, maxCost ? { max_cost_usd: maxCost } : undefined),
    getCvExtractionCacheStats: () => api.get('/admin/cv-extraction-cache/stats'),
    clearCvExtractionCache: () => api.delete('/admin/cv-extraction-cache'),
    invalidateCvExtractionCacheEntry: (sha256) => api.delete(`/admin/cv-extraction-cache/${sha256}`),
    getLlmJournalStats: (params = {}) => api.get('/admin/llm-journal/stats', { params }),
    getLlmJournal: (params = {}) => api.get('/admin/llm-journal', { params }),
    clearLlmJournal: () => api.delete('/admin/llm-journal'),
};

// Audit API (admin-only, append-only)
export const auditApi = {
    listLogs: (params = {}) => api.get('/audit/logs', { params }),
    listActions: () => api.get('/audit/actions'),
    detectAnomalies: (hours = 24, threshold = 5) => api.get('/audit/anomalies', { params: { hours, threshold } }),
    exportCsvUrl: (params = {}) => {
        const search = new URLSearchParams(params).toString();
        return `${API}/api/audit/export.csv${search ? `?${search}` : ''}`;
    },
};

// Public statistics (landing page)
export const publicApi = {
    stats: () => api.get('/public/stats'),
};

// Public + authed testimonials API
export const testimonialsApi = {
    list: (limit = 20) => api.get('/testimonials', { params: { limit } }),
    stats: () => api.get('/testimonials/stats'),
    eligibility: () => api.get('/testimonials/eligibility'),
    me: () => api.get('/testimonials/me'),
    submit: (payload) => api.post('/testimonials/submit', payload),
    // Admin moderation
    adminList: (params = {}) => api.get('/testimonials/admin', { params }),
    adminModerate: (id, published) => api.patch(`/testimonials/admin/${id}`, { published }),
    adminDelete: (id) => api.delete(`/testimonials/admin/${id}`),
};

export default api;
